import { useEffect, useState } from 'react';

function useLocalStorage<T>(
  key: string | undefined,
  defaultValue: T,
  overrideInitialValue?: T,
  validate?: (value: unknown) => boolean,
) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (overrideInitialValue) {
      return overrideInitialValue;
    }

    if (!key) return defaultValue;

    const item = window.localStorage.getItem(key);
    if (!item) return defaultValue;

    const parsed = parseJSON(item);
    if (validate && !validate(parsed)) {
      window.localStorage.removeItem(key);
      return defaultValue;
    }

    return parsed as T;
  });

  useEffect(() => {
    if (!key) return;
    window.localStorage.setItem(key, JSON.stringify(storedValue));
  }, [key, storedValue]);

  return [storedValue, setStoredValue] as const;
}

export { useLocalStorage };

function parseJSON(value: string | null) {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    // eslint-disable-next-line no-console
    console.log('parsing error on', { value });
    return undefined;
  }
}
